import { Button, Modal, pantryColors } from '@pantry/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Form, Formik, type FormikHelpers, useField } from 'formik'
import React, { useMemo } from 'react'
import { createPortal } from 'react-dom'
import * as Yup from 'yup'
import type {
  Customer,
  CustomerContactBlock,
  CustomerUpdatePayload,
} from '../../../../types/customer'
import { useCustomerDetailContext } from '../../hooks/useCustomerDetailContext'
import {
  customerDetailQueryKey,
  useUpdateCustomer,
} from '../../hooks/useCustomer'

// Edit Customer dialog. Ported from the legacy
//   src/pages/Customer/CovaCustomerDetailV2/Dialog/DialogEditCustomer.tsx
//   src/pages/Customer/GreenlineCustomerDetailV2/Dialog/DialogEditCustomer.tsx
// pair, which were near-identical and differed only in the update hook and
// detail context they consumed. Inside the Pantry Customer module we go
// through `useUpdateCustomer(adapter)`, so a single component handles both
// cova and greenline stores.
//
// Form shape mirrors `WooCustomerDetailSchema` from the main app:
//   firstName, lastName, email, phone, dob, shipping{...}, isDifferentShipping,
//   billing{...} (only when checkbox is on).
//
// DOB is stored as `MM/DD/YYYY` in the form (matching the legacy DayPicker
// format) and converted to `YYYY-MM-DDT00:00:00` on submit. The gateway
// sends it as `_shipping_dob` per the woocommerce contract.
//
// Country/state inputs are plain text — the legacy version used a Redux-
// backed country list with nested states, but the pantry-features library
// can't reach into the main-app store. Treating these as free-text strings
// is an accepted regression while we don't have a shared location-data
// utility inside the library.

const p = pantryColors

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: p.gray[600],
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[700],
  background: p.white,
  outline: 'none',
}

const errorTextStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  color: '#B91C1C',
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: p.gray[800],
  marginBottom: 4,
}

const dividerStyle: React.CSSProperties = {
  height: 1,
  background: p.gray[200],
  border: 0,
  margin: '4px 0 8px',
}

// ── Form values ────────────────────────────────────────────────────────────────

interface EditCustomerFormValues {
  firstName: string
  lastName: string
  email: string
  phone: string
  // `MM/DD/YYYY` while editing; converted to ISO on submit.
  dob: string
  shipping: {
    firstName: string
    lastName: string
    company: string
    email: string
    phone: string
    address1: string
    address2: string
    city: string
    state: string
    postcode: string
    country: string
  }
  billing: {
    firstName: string
    lastName: string
    company: string
    email: string
    phone: string
    address1: string
    address2: string
    city: string
    state: string
    postcode: string
    country: string
  }
  isDifferentShipping: boolean
}

const EMPTY_BLOCK = {
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postcode: '',
  country: '',
}

const blockToForm = (
  block?: CustomerContactBlock,
): EditCustomerFormValues['shipping'] => ({
  firstName: block?.firstName ?? '',
  lastName: block?.lastName ?? '',
  company: block?.company ?? '',
  email: block?.email ?? '',
  phone: block?.phone ?? '',
  address1: block?.address1 ?? '',
  address2: block?.address2 ?? '',
  city: block?.city ?? '',
  state: block?.state ?? '',
  postcode: block?.postcode ?? '',
  country: block?.country ?? '',
})

// Send the full 11-key shipping/billing block on every save, preserving
// empty strings rather than dropping them to `undefined`. The cova +
// greenline PUT endpoints replace the stored block with whatever we send,
// so a partial block (e.g. `{ email: "..." }` only) wipes the other 10
// fields server-side. Matches the legacy `revertDetailFormToApi` behaviour
// of always emitting all 11 keys with whatever the form holds.
const formBlockToPayload = (
  block: EditCustomerFormValues['shipping'],
): CustomerContactBlock => ({
  firstName: block.firstName ?? '',
  lastName: block.lastName ?? '',
  company: block.company ?? '',
  email: block.email ?? '',
  phone: block.phone ?? '',
  address1: block.address1 ?? '',
  address2: block.address2 ?? '',
  city: block.city ?? '',
  state: block.state ?? '',
  postcode: block.postcode ?? '',
  country: block.country ?? '',
})

// Mirror legacy `initialIsDifferentShipping`: compare 11 keys with
// trim+lowercase normalisation, return true if any differ.
const computeIsDifferentShipping = (
  shipping?: CustomerContactBlock,
  billing?: CustomerContactBlock,
): boolean => {
  const norm = (v: string | undefined) => (v ?? '').toString().trim().toLowerCase()
  const keys: (keyof CustomerContactBlock)[] = [
    'firstName',
    'lastName',
    'company',
    'email',
    'phone',
    'address1',
    'address2',
    'city',
    'state',
    'country',
    'postcode',
  ]
  return keys.some((key) => norm(shipping?.[key]) !== norm(billing?.[key]))
}

// Convert `MM/DD/YYYY` form value to `YYYY-MM-DDT00:00:00` API value.
const toApiDob = (value: string): string => {
  if (!value) return ''
  // Accept either `MM/DD/YYYY` or any value Date can parse.
  const slashed = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slashed) {
    const [, mm, dd, yyyy] = slashed
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T00:00:00`
}

// Convert ISO/legacy DOB value to `MM/DD/YYYY` for the form input.
const toFormDob = (value: string | undefined): string => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}/${dd}/${yyyy}`
}

// ── Validation ─────────────────────────────────────────────────────────────────

// Lightweight equivalent of the main-app `WooCustomerDetailSchema`. We keep
// only the required-firstName + email-format checks; the legacy schema also
// enforced shipping/billing address validation but the design treats those
// as soft fields, and the API will reject hard problems with a 4xx.
const EditCustomerSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim(),
  email: Yup.string().trim().email('Enter a valid email'),
  phone: Yup.string().trim(),
  dob: Yup.string().test(
    'dob-format',
    'Enter a valid date (MM/DD/YYYY)',
    (value) => !value || /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(value.trim()),
  ),
})

// ── Form Field bound to Formik ─────────────────────────────────────────────────

interface TextFieldProps {
  name: string
  label: string
  type?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
}

const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  disabled,
  required,
  autoFocus,
}) => {
  const [field, meta] = useField(name)
  const showError = Boolean(meta.touched && meta.error)
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required ? <span style={{ color: '#B91C1C' }}> *</span> : null}
      </label>
      <input
        {...field}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        style={{
          ...inputStyle,
          borderColor: showError ? '#B91C1C' : p.gray[200],
        }}
      />
      {showError ? <div style={errorTextStyle}>{meta.error}</div> : null}
    </div>
  )
}

// ── Initial values ─────────────────────────────────────────────────────────────

const buildInitialValues = (
  customer: Customer | undefined,
): EditCustomerFormValues => {
  const shipping = customer?.shipping
  const billing = customer?.billing
  const isDifferent = computeIsDifferentShipping(shipping, billing)

  // Names: prefer customer.firstName/lastName (already populated by the
  // mapper), then the shipping block, then the billing block.
  const firstName =
    customer?.firstName || shipping?.firstName || billing?.firstName || ''
  const lastName =
    customer?.lastName || shipping?.lastName || billing?.lastName || ''

  return {
    firstName,
    lastName,
    email: customer?.email ?? '',
    phone: customer?.phone ?? '',
    dob: toFormDob(customer?.dateOfBirth),
    shipping: blockToForm(shipping),
    billing: blockToForm(billing),
    isDifferentShipping: isDifferent,
  }
}

// ── Dialog ─────────────────────────────────────────────────────────────────────

interface CustomerEditDialogProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

export const CustomerEditDialog: React.FC<CustomerEditDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { customer, customerId, adapter } = useCustomerDetailContext()
  const { mutateAsync, isPending } = useUpdateCustomer(adapter)
  const queryClient = useQueryClient()

  const initialDob = useMemo(
    () => toFormDob(customer?.dateOfBirth),
    [customer?.dateOfBirth],
  )

  const initialValues = useMemo(
    () => buildInitialValues(customer),
    [customer],
  )

  // The PUT endpoint addresses the customer by their woocommerce id
  // (`/cova|greenline/stores/:storeId/customers/:wooCustomerId`), not the
  // POS-side id we use for the detail GET. A customer with no woo id is a
  // POS-only / guest record that can't be updated through this endpoint —
  // the Edit button on `CustomerContactCard` is also gated on this flag so
  // the dialog should never open without one, but we guard here defensively.
  const wooCustomerId =
    customer?.wooCustomerId !== undefined && customer?.wooCustomerId !== null
      ? String(customer.wooCustomerId)
      : ''

  if (!open || !customer || !wooCustomerId) {
    return null
  }

  const handleSubmit = async (
    values: EditCustomerFormValues,
    helpers: FormikHelpers<EditCustomerFormValues>,
  ) => {
    helpers.setStatus(undefined)
    try {
      const shippingPayload = formBlockToPayload(values.shipping)
      const billingPayload = values.isDifferentShipping
        ? formBlockToPayload(values.billing)
        : shippingPayload

      // Only send DOB when the user changed it — matches the legacy
      // `delete mappedValues._shipping_dob` branch.
      const dobChanged = values.dob !== initialDob

      const payload: CustomerUpdatePayload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        shipping: shippingPayload,
        billing: billingPayload,
      }

      if (dobChanged) {
        payload.dateOfBirth = toApiDob(values.dob)
      }

      // Send the woo id as the URL identifier — this matches the legacy
      // `updateCovaCustomerDetail({ storeId, wooId, ... })` call shape, and
      // is what the backend expects on the PUT path.
      await mutateAsync({ customerId: wooCustomerId, payload })

      // `useUpdateCustomer` invalidates the detail query keyed by whatever
      // id it was called with — i.e. the woo id. Our detail GET is keyed by
      // the POS customerId, so we additionally invalidate that key here to
      // pick up the updated record on the open profile page.
      if (customerId && customerId !== wooCustomerId) {
        queryClient.invalidateQueries({
          queryKey: customerDetailQueryKey(
            adapter.storeType,
            adapter.storeId,
            customerId,
          ),
        })
      }

      onSaved?.()
      onClose()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to update customer. Please try again.'
      helpers.setStatus(message)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={EditCustomerSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, dirty, isValid, status, submitForm, setFieldValue }) => {
        const footer = (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitForm}
              disabled={isPending || !dirty || !isValid}
            >
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )

        // Portal to document.body so the modal escapes any ancestor
        // stacking context (the customer profile page header sits in a
        // parent that traps a non-portaled fixed-position child beneath
        // it regardless of zIndex). createPortal lifts the whole Modal
        // tree — overlay + dialog — out to the document root, where
        // zIndex 2000 wins cleanly.
        const modal = (
          <Modal
            open={open}
            onClose={isPending ? () => {} : onClose}
            title="Edit Customer Info"
            width={640}
            footer={footer}
            zIndex={2000}
          >
            <Form>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  // Modal box has no built-in scroll. Cap the form body so
                  // long forms scroll inside the dialog instead of pushing
                  // the footer off-screen. The 240px budget covers the
                  // outer 48px viewport padding + modal header (~70px) +
                  // footer (~60px) + body padding (~48px).
                  maxHeight: 'calc(100vh - 240px)',
                  overflowY: 'auto',
                  // Pull the scroll surface to the modal padding edges so
                  // the scrollbar sits flush against the modal wall, then
                  // restore the gutter via inline padding.
                  margin: '-24px -24px',
                  padding: '24px 24px',
                }}
              >
                {status ? (
                  <div
                    role="alert"
                    style={{
                      padding: '8px 12px',
                      fontSize: 12.5,
                      color: '#B91C1C',
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: 6,
                    }}
                  >
                    {status}
                  </div>
                ) : null}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}
                >
                  <TextField
                    name="firstName"
                    label="First name"
                    required
                    autoFocus
                    disabled={isPending}
                  />
                  <TextField
                    name="lastName"
                    label="Last name"
                    disabled={isPending}
                  />
                  <TextField
                    name="email"
                    label="Email"
                    type="email"
                    disabled={isPending}
                  />
                  <TextField
                    name="phone"
                    label="Phone number"
                    disabled={isPending}
                  />
                  <TextField
                    name="dob"
                    label="Date of birth"
                    placeholder="MM/DD/YYYY"
                    disabled={isPending}
                  />
                </div>

                <hr style={dividerStyle} />
                <div style={sectionTitleStyle}>Shipping address</div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}
                >
                  <TextField
                    name="shipping.email"
                    label="Shipping email"
                    type="email"
                    disabled={isPending}
                  />
                  <TextField
                    name="shipping.phone"
                    label="Shipping phone"
                    disabled={isPending}
                  />
                </div>

                <TextField
                  name="shipping.address1"
                  label="Address line 1"
                  disabled={isPending}
                />
                <TextField
                  name="shipping.address2"
                  label="Address line 2"
                  disabled={isPending}
                />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}
                >
                  <TextField
                    name="shipping.country"
                    label="Country"
                    disabled={isPending}
                  />
                  <TextField
                    name="shipping.city"
                    label="City"
                    disabled={isPending}
                  />
                  <TextField
                    name="shipping.state"
                    label="Province / State"
                    disabled={isPending}
                  />
                  <TextField
                    name="shipping.postcode"
                    label="Postal code"
                    disabled={isPending}
                  />
                </div>

                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    color: p.gray[700],
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={values.isDifferentShipping}
                    disabled={isPending}
                    onChange={(e) =>
                      setFieldValue('isDifferentShipping', e.target.checked)
                    }
                  />
                  Enter different billing address
                </label>

                {values.isDifferentShipping ? (
                  <>
                    <hr style={dividerStyle} />
                    <div style={sectionTitleStyle}>Billing address</div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 12,
                      }}
                    >
                      <TextField
                        name="billing.email"
                        label="Billing email"
                        type="email"
                        disabled={isPending}
                      />
                      <TextField
                        name="billing.phone"
                        label="Billing phone"
                        disabled={isPending}
                      />
                    </div>

                    <TextField
                      name="billing.address1"
                      label="Address line 1"
                      disabled={isPending}
                    />
                    <TextField
                      name="billing.address2"
                      label="Address line 2"
                      disabled={isPending}
                    />

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 12,
                      }}
                    >
                      <TextField
                        name="billing.country"
                        label="Country"
                        disabled={isPending}
                      />
                      <TextField
                        name="billing.city"
                        label="City"
                        disabled={isPending}
                      />
                      <TextField
                        name="billing.state"
                        label="Province / State"
                        disabled={isPending}
                      />
                      <TextField
                        name="billing.postcode"
                        label="Postal code"
                        disabled={isPending}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            </Form>
          </Modal>
        )

        return createPortal(modal, document.body)
      }}
    </Formik>
  )
}
